import React, { Suspense, lazy, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import AuraChat, { AuraOrb } from "./AuraChat";
import { useAura } from "./AuraProvider";
import AuraDock from "./AuraDock";
import { storage } from "../../lib/aura";

const TEASER_DELAY_MS = 6000;
const TEASER_DISMISSED = "aura_teaser_dismissed";
const PHONE = "(max-width: 639px)";

// Phones get Simmi as a full-height bottom sheet instead of a floating window (loaded only on phones).
const AuraSheet = lazy(() => import("./AuraSheet"));

const useIsPhone = () => {
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.matchMedia?.(PHONE).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE);
    if (!mq) return undefined;
    const update = () => setPhone(mq.matches);
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return phone;
};

// Floating Simmi on every page. On the home page it stays out of the way while the hero agent is on screen.
const AuraLauncher = () => {
  const { open, setOpen, heroVisible, hasConversation, config, t } = useAura();
  const isPhone = useIsPhone();
  const { pathname } = useLocation();
  const heroOnScreen = pathname === "/" && heroVisible;
  const [teaser, setTeaser] = useState(false);

  // Proactive greeting, like Docket: a small nudge after a few seconds, once per browser session.
  useEffect(() => {
    if (hasConversation || storage.get("sessionStorage", TEASER_DISMISSED)) return undefined;
    const timer = setTimeout(() => setTeaser(true), TEASER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [hasConversation, pathname]);

  const dismissTeaser = () => {
    setTeaser(false);
    storage.set("sessionStorage", TEASER_DISMISSED, "1");
  };

  const openChat = () => {
    dismissTeaser();
    setOpen(true);
  };

  useEffect(() => {
    const handleOpen = () => {
      dismissTeaser();
      setOpen(true);
    };
    window.addEventListener("open-aura-agent", handleOpen);
    return () => window.removeEventListener("open-aura-agent", handleOpen);
  }, [setOpen]);

  // With the Anam avatar configured, Simmi is Docket-style: docked on every page, voice-first, and text
  // chat happens in the dock too (no rectangular window).
  if (config.avatar) return <AuraDock />;
  if (open && isPhone) {
    return (
      <Suspense fallback={null}>
        <AuraSheet onClose={() => setOpen(false)} />
      </Suspense>
    );
  }
  if (open) {
    return (
      <div className="fixed bottom-4 right-4 z-[60] sm:bottom-6 sm:right-6">
        <AuraChat variant="floating" onClose={() => setOpen(false)} />
      </div>
    );
  }
  if (heroOnScreen) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-3 sm:bottom-6 sm:right-6">
      {teaser && !hasConversation && (
        <div className="aura-msg relative max-w-[260px] rounded-[16px] rounded-br-[4px] border border-[#334155]/70 bg-[#0B1B36] px-4 py-3 text-[13px] leading-[19px] text-white/90 shadow-2xl">
          <button
            onClick={dismissTeaser}
            aria-label="Dismiss"
            className="absolute -left-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-[#050B15] text-white/70 hover:text-white"
          >
            <X className="h-3 w-3" />
          </button>
          <button onClick={openChat} className="text-left">
            <span className="font-semibold text-white">Hi, I'm Simmi 👋</span>
            <br />
            Curious where AI could make the biggest difference in your business? Ask me, or just talk.
          </button>
        </div>
      )}
      <button
        onClick={openChat}
        aria-label="Chat with Simmi"
        title={t.shortcut}
        className="flex items-center gap-2.5 rounded-full border border-[#334155] py-2 pl-2 pr-4 text-[14px] font-medium text-white shadow-2xl transition-transform hover:scale-[1.03]"
        style={{ background: "linear-gradient(180deg, #0B1B36 0%, #050B15 100%)", boxShadow: "0 8px 32px rgba(37,99,235,0.35)" }}
      >
        <AuraOrb size={34} />
        Ask Simmi
      </button>
    </div>
  );
};

export default AuraLauncher;
